import { appendOriginLaunchSurface, detectLaunchContext, type LaunchSurface } from "@common-ui/utilities/launchContext";
import { getUploadDebugPreference } from "./uploadDebugPreference";

export function appendSparkyAuthTokenForAdminAsync(
	url: string,
	token: string | null | undefined,
	baseUrl = window.location.origin
): string {
	const parsedUrl = new URL(url, baseUrl);
	if (parsedUrl.searchParams.get("customerasyncrequest") === "true") return url;

	parsedUrl.searchParams.set("customerasyncrequest", "false");

	if (token) {
		const hashParams = new URLSearchParams(parsedUrl.hash.startsWith("#") ? parsedUrl.hash.slice(1) : parsedUrl.hash);
		hashParams.set("auth_token", token);
		parsedUrl.hash = hashParams.toString();
	}

	return parsedUrl.toString();
}

export async function getSparkyAuthTokenForAdminAsync(authService: {
	runningCallback?: boolean;
	waitForCallbackToFinish?: () => Promise<void>;
	getAccessToken?: (options?: { redirectOnAuthFailure?: boolean }) => Promise<string | null>;
}): Promise<string | null> {
	try {
		if (authService.runningCallback) {
			await authService.waitForCallbackToFinish?.();
		}

		if (typeof authService.getAccessToken !== "function") return null;
		return await authService.getAccessToken({ redirectOnAuthFailure: false });
	} catch {
		return null;
	}
}

/**
 * LSP-21256: the URL Admin hands to the async recorder — the admin-owned marker and auth fragment above,
 * plus the dashboard's own launch surface so the recorder can report where it was started from. The
 * surface is detected at hand-off time, never cached, so a stale launch can never be passed on.
 */
export async function buildAdminAsyncLaunchUrl(
	url: string,
	authService: Parameters<typeof getSparkyAuthTokenForAdminAsync>[0],
	launchSurface: LaunchSurface = detectLaunchContext().launchSurface
): Promise<string> {
	if (!url) return url;
	const uploadDebug = getUploadDebugPreference();
	const token = await getSparkyAuthTokenForAdminAsync(authService);
	const launchUrl = new URL(appendOriginLaunchSurface(appendSparkyAuthTokenForAdminAsync(url, token), launchSurface));
	if (launchUrl.searchParams.get("customerasyncrequest") !== "true" && (uploadDebug === "1" || uploadDebug === "0")) {
		launchUrl.searchParams.set("uploadDebug", uploadDebug);
	}
	console.debug("[Upload diagnostics] Admin recorder handoff", {
		preference: uploadDebug,
		customerRequest: launchUrl.searchParams.get("customerasyncrequest") === "true"
	});
	return launchUrl.toString();
}
